import { ItemCount, Status } from 'types/items';

/**
 * Reshaping for the Items by Locations tab (ClickUp 86bbmcuwa).
 *
 * The table is a pivot: locations down the side, SKUs across the top. Wash hub
 * rows come from the snapshot's per-status breakdown so they agree with the
 * Subtotal At Hubs column on the Item Summary tab; site rows come from the
 * snapshot's per-location counts.
 */

type Breakdown = { [status: string]: number } | undefined;

/** Which of the two views the page is showing. */
export type SummaryTab = 'summary' | 'locations';

export const isSummaryTab = (value: unknown): value is SummaryTab =>
  value === 'summary' || value === 'locations';

/** A SKU column. */
export type LocationSku = {
  _id: string;
  name: string;
};

/** One `{venue, sku, count}` cell as the snapshot stores it. */
export type LocationCount = {
  venue: string;
  sku: string;
  count: number;
};

/**
 * One column per SKU. At Global View the API used to send a row per hub for
 * each SKU (ClickUp 86bcej684); keying on _id keeps that from turning into a
 * column per hub that all read the same cell.
 */
export const uniqueSkus = (
  items: Array<{ _id: unknown; name: string }>,
): LocationSku[] => {
  const seen = new Map<string, LocationSku>();
  items.forEach((item) => {
    const id = String(item._id);
    if (!seen.has(id)) {
      seen.set(id, { _id: id, name: item.name });
    }
  });
  return Array.from(seen.values());
};

/** A location that can hold items: the wash hub itself, or a customer site. */
export type LocationRef = {
  _id: string;
  name: string;
};

export type LocationRow = {
  key: string;
  location: string;
  washHub: string;
  counts: { [skuId: string]: number };
};

/**
 * Statuses that mean the item is at the hub and available, rather than packed,
 * delivered or written off. Mirrors the Item Summary's In Process column;
 * the API repeats this list in counts.service processCountCsv.
 */
const IN_PROCESS_STATUSES = [
  Status.WASHED,
  Status.DRY,
  Status.UNUSED,
  Status.UNPACK,
  Status.OPEN,
  Status.RECONCILED_FOUND,
];

export const calcInProcess = (breakdown: Breakdown): number => {
  if (!breakdown) {
    return 0;
  }

  return IN_PROCESS_STATUSES.reduce(
    (sum, status) => sum + (breakdown[status] || 0),
    0,
  );
};

/** In process plus packed, i.e. everything physically at the hub. */
export const calcSubtotalAtHubs = (breakdown: Breakdown): number => {
  if (!breakdown) {
    return 0;
  }

  return calcInProcess(breakdown) + (breakdown[Status.PACKED] || 0);
};

/**
 * Builds the pivot rows: the wash hub first, then its active sites by name.
 *
 * Sites come from the active-site list rather than from the counts, so a site
 * holding nothing still gets a row of zeros, and a count belonging to a
 * suspended or archived site cannot put that site back on the table.
 */
export const buildLocationRows = ({
  skus,
  hub,
  items,
  byLocation,
  sites,
}: {
  skus: LocationSku[];
  hub: LocationRef & { washHub: string };
  items: ItemCount[];
  byLocation: LocationCount[];
  sites: LocationRef[];
}): LocationRow[] => {
  const emptyCounts = () =>
    skus.reduce<{ [skuId: string]: number }>((acc, sku) => {
      acc[sku._id] = 0;
      return acc;
    }, {});

  const hubCounts = emptyCounts();
  items.forEach((item) => {
    if (item._id in hubCounts) {
      // Summed, not assigned: a SKU can arrive once per hub at Global View.
      hubCounts[item._id] += calcSubtotalAtHubs(item.breakdown);
    }
  });

  const hubRow: LocationRow = {
    key: hub._id,
    location: hub.name,
    washHub: hub.washHub,
    counts: hubCounts,
  };

  const siteRows = [...sites]
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }))
    .map((site) => {
      const counts = emptyCounts();

      byLocation
        .filter((cell) => cell.venue === site._id && cell.sku in counts)
        .forEach((cell) => {
          counts[cell.sku] += cell.count;
        });

      return {
        key: site._id,
        location: site.name,
        washHub: hub.washHub,
        counts,
      };
    });

  return [hubRow, ...siteRows];
};

/** Wraps a field for CSV if it holds a comma, quote or newline. */
const csvField = (value: string | number): string => {
  const text = String(value ?? '');

  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * The pivot as CSV, matching what is on screen.
 *
 * Built here rather than server-side like the Item Summary export: the rows
 * are already in hand, so this needs no endpoint and cannot drift from the
 * table it is exporting.
 */
export const buildLocationCsv = (
  rows: LocationRow[],
  skus: LocationSku[],
  totals: { [skuId: string]: number },
): string => {
  const header = ['Location', 'Wash Hub', ...skus.map((sku) => sku.name)];

  const body = rows.map((row) => [
    row.location,
    row.washHub,
    ...skus.map((sku) => row.counts[sku._id] || 0),
  ]);

  const footer = [
    'Total Items',
    '',
    ...skus.map((sku) => totals[sku._id] || 0),
  ];

  return [header, ...body, footer]
    .map((line) => line.map(csvField).join(','))
    .join('\n');
};

/** The Total Items footer: each SKU column summed down the rows. */
export const sumColumnTotals = (
  rows: LocationRow[],
  skus: LocationSku[],
): { [skuId: string]: number } =>
  skus.reduce<{ [skuId: string]: number }>((acc, sku) => {
    acc[sku._id] = rows.reduce(
      (sum, row) => sum + (row.counts[sku._id] || 0),
      0,
    );
    return acc;
  }, {});
